<script>
import { useVuelidate } from '@vuelidate/core';
import { email } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { frontendURL } from 'dashboard/helper/URLHelper';
import {
  buildPhoneInputState,
  CONTACT_ENTITY_TYPES,
} from 'dashboard/helper/contactEntity';
import { findContactsByEntity } from 'dashboard/helper/contactEntityLookup';
import parsePhoneNumber from 'libphonenumber-js/max';
import {
  normalizePhoneForInput,
  PHONE_VALIDATION_ERRORS,
  validatePhoneForInput,
} from 'shared/helpers/phoneNumberValidation';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import { mapGetters } from 'vuex';

export default {
  name: 'NewContactModal',
  computed: {
    ...mapGetters({
      currentAccountId: 'getCurrentAccountId',
    }),
    parsePhoneNumber() {
      return parsePhoneNumber(this.phoneNumber);
    },
    isPhoneNumberNotValid() {
      if (!this.phoneNumber) return false;
      return !this.phoneValidation.isValid;
    },
    phoneNumberError() {
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.MISSING_DIAL_CODE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DIAL_CODE_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.INVALID_BRAZIL_AREA_CODE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.BRAZIL_AREA_CODE_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.PHONE_TOO_SHORT
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.TOO_SHORT_ERROR');
      }
      if (
        this.phoneValidation.errorKey === PHONE_VALIDATION_ERRORS.PHONE_TOO_LONG
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.TOO_LONG_ERROR');
      }
      if (
        this.phoneValidation.errorKey ===
        PHONE_VALIDATION_ERRORS.PHONE_INVALID_LENGTH
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.INVALID_LENGTH_ERROR');
      }
      if (
        this.phoneValidation.errorKey === PHONE_VALIDATION_ERRORS.INVALID_PHONE
      ) {
        return this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.ERROR');
      }
      return '';
    },
    normalizedPhoneNumber() {
      return normalizePhoneForInput(this.phoneNumber, this.activeDialCode);
    },
    phoneValidation() {
      return validatePhoneForInput(this.phoneNumber, this.activeDialCode);
    },
    setPhoneNumber() {
      if (this.parsePhoneNumber && this.parsePhoneNumber.countryCallingCode) {
        return this.phoneNumber;
      }
      if (this.phoneNumber === '' && this.activeDialCode !== '') {
        return '';
      }
      return this.normalizedPhoneNumber;
    },
    hasAtLeastOneContactField() {
      return Boolean(this.email?.trim() || this.phoneNumber?.trim());
    },
    shouldShowRequiredFieldWarning() {
      return !this.hasAtLeastOneContactField && this.v$.$dirty;
    },
    contactsSearchBaseUrl() {
      return frontendURL(`accounts/${this.currentAccountId}/contacts`);
    },
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    initialState: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      inProgress: false,
      name: '',
      email: '',
      phoneNumber: '',
      activeDialCode: '',
    };
  },
  validations() {
    return {
      email: {
        email,
      },
      phoneNumber: {},
    };
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.applyInitialState();
        return;
      }

      this.resetForm();
    },
  },
  mounted() {
    if (this.show) {
      this.applyInitialState();
    }
  },
  methods: {
    applyInitialState() {
      this.resetForm();
      this.email = this.initialState?.email || '';

      if (this.initialState?.prefilledPhone) {
        const { phoneNumber, activeDialCode } = buildPhoneInputState(
          this.initialState.prefilledPhone
        );
        this.phoneNumber = phoneNumber;
        this.activeDialCode = activeDialCode;
      }
    },
    resetForm() {
      this.name = '';
      this.email = '';
      this.phoneNumber = '';
      this.activeDialCode = '';
      this.v$.$reset();
    },
    onClose() {
      this.$emit('close');
      this.resetForm();
    },
    onPhoneNumberInputChange(value, code) {
      this.activeDialCode = code;
    },
    setPhoneCode(code) {
      if (this.phoneNumber !== '' && this.parsePhoneNumber) {
        const dialCode = this.parsePhoneNumber.countryCallingCode;
        if (dialCode === code) {
          return;
        }
        this.activeDialCode = `+${dialCode}`;
        const newPhoneNumber = this.phoneNumber.replace(
          `+${dialCode}`,
          `${code}`
        );
        this.phoneNumber = newPhoneNumber;
      } else {
        this.activeDialCode = code;
      }
    },
    getContactObject() {
      const contactObject = {
        name: this.name,
      };

      if (this.email.trim()) {
        contactObject.email = this.email.trim();
      }

      if (this.setPhoneNumber.trim()) {
        contactObject.phone_number = this.setPhoneNumber;
      }

      return contactObject;
    },
    async findExistingContactForDuplicate() {
      const entityType = this.email.trim()
        ? CONTACT_ENTITY_TYPES.EMAIL
        : CONTACT_ENTITY_TYPES.PHONE;
      const entityValue = this.email.trim() || this.setPhoneNumber;
      return findContactsByEntity(entityType, entityValue);
    },
    async handleSubmit() {
      this.v$.$touch();
      if (
        this.v$.$invalid ||
        this.isPhoneNumberNotValid ||
        !this.hasAtLeastOneContactField
      ) {
        return;
      }

      this.inProgress = true;
      try {
        await this.$store.dispatch('contacts/create', this.getContactObject());
        useAlert(this.$t('SIDEBAR.NEW_CONTACT_MODAL.API.SUCCESS_MESSAGE'));
        this.onClose();
      } catch (error) {
        if (error instanceof DuplicateContactException) {
          const matchingContacts = await this.findExistingContactForDuplicate();
          if (matchingContacts.length === 1) {
            const existingContact = matchingContacts[0];
            const action = {
              type: 'link',
              to: frontendURL(
                `accounts/${this.currentAccountId}/contacts/${existingContact.id}`
              ),
              message: this.$t('CONVERSATION.ENTITY_CONTEXT_MENU.VIEW_CONTACT'),
              newTab: true,
            };
            const duplicateMessage = error.data.includes('email')
              ? this.$t(
                  'CONVERSATION.ENTITY_CONTEXT_MENU.EXISTING_EMAIL_CONTACT'
                )
              : this.$t(
                  'CONVERSATION.ENTITY_CONTEXT_MENU.EXISTING_PHONE_CONTACT'
                );
            useAlert(duplicateMessage, action);
            return;
          }

          if (matchingContacts.length > 1) {
            const searchValue = this.email.trim() || this.setPhoneNumber;
            const action = {
              type: 'link',
              to: `${this.contactsSearchBaseUrl}?q=${encodeURIComponent(
                searchValue
              )}`,
              message: this.$t('CONVERSATION.ENTITY_CONTEXT_MENU.VIEW_CONTACT'),
              newTab: true,
            };
            const duplicateMessage = error.data.includes('email')
              ? this.$t(
                  'CONVERSATION.ENTITY_CONTEXT_MENU.MULTIPLE_EMAIL_CONTACTS'
                )
              : this.$t(
                  'CONVERSATION.ENTITY_CONTEXT_MENU.MULTIPLE_PHONE_CONTACTS'
                );
            useAlert(duplicateMessage, action);
            return;
          }

          if (error.data.includes('email')) {
            useAlert(this.$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.DUPLICATE'));
          } else {
            useAlert(this.$t('CONTACT_FORM.FORM.PHONE_NUMBER.DUPLICATE'));
          }
        } else if (error instanceof ExceptionWithMessage) {
          useAlert(error.data);
        } else {
          useAlert(this.$t('SIDEBAR.NEW_CONTACT_MODAL.API.ERROR_MESSAGE'));
        }
      } finally {
        this.inProgress = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show.sync="show" :on-close="onClose">
    <woot-modal-header
      :header-title="$t('SIDEBAR.NEW_CONTACT_MODAL.TITLE')"
      :header-content="$t('SIDEBAR.NEW_CONTACT_MODAL.DESCRIPTION')"
    />
    <form class="w-full" @submit.prevent="handleSubmit">
      <div class="p-8">
        <label>
          {{ $t('CONTACT_FORM.FORM.NAME.LABEL') }}
          <input
            v-model.trim="name"
            type="text"
            :placeholder="$t('CONTACT_FORM.FORM.NAME.PLACEHOLDER')"
          />
        </label>
        <label :class="{ error: v$.email.$error }">
          {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.LABEL') }}
          <input
            v-model.trim="email"
            type="email"
            :placeholder="$t('CONTACT_FORM.FORM.EMAIL_ADDRESS.PLACEHOLDER')"
            @input="v$.email.$touch"
          />
          <span v-if="v$.email.$error" class="message">
            {{ $t('CONTACT_FORM.FORM.EMAIL_ADDRESS.ERROR') }}
          </span>
        </label>
        <label
          :class="{
            error: isPhoneNumberNotValid,
          }"
        >
          {{ $t('CONTACT_FORM.FORM.PHONE_NUMBER.LABEL') }}
          <woot-phone-input
            v-model="phoneNumber"
            :value="phoneNumber"
            :dial-code="activeDialCode"
            :error="isPhoneNumberNotValid"
            :placeholder="$t('CONTACT_FORM.FORM.PHONE_NUMBER.PLACEHOLDER')"
            @input="onPhoneNumberInputChange"
            @blur="v$.phoneNumber.$touch"
            @setCode="setPhoneCode"
          />
          <span v-if="isPhoneNumberNotValid" class="message">
            {{ phoneNumberError }}
          </span>
        </label>
        <span
          v-if="shouldShowRequiredFieldWarning"
          class="message block text-red-600 dark:text-red-400"
        >
          {{ $t('CONTACT_FORM.FORM.REQUIRED_CONTACT_FIELD') }}
        </span>
      </div>
      <div class="flex justify-end w-full gap-2 px-8 py-4">
        <woot-button
          class="mr-2"
          variant="clear"
          type="button"
          @click="onClose"
        >
          {{ $t('SIDEBAR.NEW_CONTACT_MODAL.BUTTONS.CANCEL') }}
        </woot-button>
        <woot-button :is-loading="inProgress" button-type="submit">
          {{ $t('SIDEBAR.NEW_CONTACT_MODAL.BUTTONS.CREATE') }}
        </woot-button>
      </div>
    </form>
  </woot-modal>
</template>
